import axios from "axios";

// Content API
const Content = {
    async GetById(id, languageCode = "tr") {
        const response = await axios.get(`/api/website/admin/content/${id}?lang=${languageCode}`);
        return response;
    },
    async Create(data) {
        const response = await axios.post("/api/website/admin/content", data);
        return response;
    },
    async Update(id, data) {
        const response = await axios.post(`/api/website/admin/content/${id}`, data);
        return response;
    },
    async Delete(id) {
        const response = await axios.post(`/api/website/admin/content/${id}/delete`);
        return response;
    },
    async GetContents(data) {
        const response = await axios.post("/api/website/public/contents", data);
        return response;
    },
    async GetBySlug(slug, languageCode = "tr") {
        const response = await axios.get(`/api/website/public/content/${slug}?lang=${languageCode}`);
        return response;
    },
    async GetByKey(contentKey, languageCode = "tr") {
        const response = await axios.get(`/api/website/public/content/key/${contentKey}?lang=${languageCode}`);
        return response;
    },
    async GetByCategory(categoryKey, languageCode = "tr") {
        const response = await axios.get(`/api/website/public/category/${categoryKey}?lang=${languageCode}`);
        return response;
    },
    async GetFeatured(languageCode = "tr", take = 10) {
        const response = await axios.get(`/api/website/public/featured?lang=${languageCode}&take=${take}`);
        return response;
    },
    async GetAsJson(contentKey, languageCode = "tr") {
        const response = await axios.get(`/api/website/public/json/${contentKey}?lang=${languageCode}`);
        return response;
    },
    async IncrementView(contentId) {
        const response = await axios.post(`/api/website/public/view/${contentId}`);
        return response;
    },
};

// Language API
const Language = {
    async GetAll() {
        const response = await axios.get("/api/website/admin/languages");
        return response;
    },
    async GetById(id) {
        const response = await axios.get(`/api/website/admin/language/${id}`);
        return response;
    },
    async Create(data) {
        const response = await axios.post("/api/website/admin/language", data);
        return response;
    },
    async Update(id, data) {
        const response = await axios.post(`/api/website/admin/language/${id}`, data);
        return response;
    },
    async Delete(id) {
        const response = await axios.delete(`/api/website/admin/language/${id}`);
        return response;
    },
    async ChangeStatus(id) {
        const response = await axios.post(`/api/website/admin/language/${id}/changestatus`);
        return response;
    },
};

// Navigation API
const Navigation = {
    // PUBLIC ENDPOINTS
    async GetMenu(navigationType, languageCode = "tr") {
        const response = await axios.get(`/api/website/public/navigation/${navigationType}?lang=${languageCode}`);
        return response;
    },
    async GetTree(navigationType, languageCode = "tr") {
        const response = await axios.get(`/api/website/public/navigation/tree`, {
            params: { lang: languageCode, navigationType: navigationType },
        });
        return response;
    },
    /**
     * Navigation ağacını TÜM içeriklerle birlikte getirir (Ağır sorgu - caching önerilir)
     * @param {string} navigationType - header, footer, sidebar, mobile
     * @param {string} languageCode - Dil kodu
     */
    async GetTreeWithContents(navigationType, languageCode = "tr") {
        const response = await axios.get(`/api/website/public/navigation/${navigationType}/tree-with-contents`, {
            params: { lang: languageCode },
        });
        return response;
    },
    /**
     * Belirli bir navigasyon anahtarına ait tüm içerikleri getirir
     * @param {string} navigationKey - Navigation benzersiz anahtarı (örn: "products", "services")
     * @param {string} languageCode - Dil kodu
     * @param {number|null} skip - Pagination başlangıç
     * @param {number|null} take - Kayıt sayısı
     */
    async GetContentsByNavigationKey(navigationKey, languageCode = "tr", skip = null, take = null) {
        const params = { lang: languageCode };
        if (skip !== null) params.skip = skip;
        if (take !== null) params.take = take;
        const response = await axios.get(`/api/website/public/navigation/${navigationKey}/contents`, { params });
        return response;
    },
    /**
     * Birden fazla navigation ID'sine göre içerik sorgulama
     * @param {GetContentsByNavigationsRequest} data - Request modeli
     */
    async GetContentsByNavigationIds(data) {
        const response = await axios.post("/api/website/public/navigation/contents", data);
        return response;
    },
    async GetAsJson(navigationType, languageCode = "tr") {
        const response = await axios.get(`/api/website/public/navigation/${navigationType}/json?lang=${languageCode}`);
        return response;
    },

    // ADMIN ENDPOINTS
    /**
     * Navigation detayı getir (contents array dahil)
     * @param {string} id - Navigation ID (Guid)
     * @param {string} languageCode - Dil kodu
     */
    async GetById(id, languageCode = "tr") {
        const response = await axios.get(`/api/website/admin/navigation/${id}?lang=${languageCode}`);
        return response;
    },
    /**
     * Yeni navigation oluştur (contentIds desteği ile)
     * @param {Object} data - CreateNavigationRequest (contentIds: NavigationContentModel[])
     */
    async Create(data) {
        const response = await axios.post("/api/website/admin/navigation", data);
        return response;
    },
    /**
     * Navigation güncelle (contentIds desteği ile)
     * @param {string} id - Navigation ID
     * @param {Object} data - CreateNavigationRequest (contentIds: NavigationContentModel[])
     */
    async Update(id, data) {
        const response = await axios.post(`/api/website/admin/navigation/${id}`, data);
        return response;
    },
    async Delete(id) {
        const response = await axios.post(`/api/website/admin/navigation/${id}/delete`);
        return response;
    },
    async UpdateOrder(data) {
        const response = await axios.post("/api/website/admin/navigation/updateorder", data);
        return response;
    },
};

// Configuration API
const Configuration = {
    async GetAll(languageCode = null) {
        const url = languageCode ? `/api/website/admin/configurations?lang=${languageCode}` : "/api/website/admin/configurations";
        const response = await axios.get(url);
        return response;
    },
    async GetByGroup(group, languageCode = null) {
        const url = languageCode ? `/api/website/admin/configuration/group/${group}?lang=${languageCode}` : `/api/website/admin/configuration/group/${group}`;
        const response = await axios.get(url);
        return response;
    },
    async GetByKey(key, languageCode = null) {
        const url = languageCode ? `/api/website/admin/configuration/${key}?lang=${languageCode}` : `/api/website/admin/configuration/${key}`;
        const response = await axios.get(url);
        return response;
    },
    async Create(data) {
        const response = await axios.post("/api/website/admin/configuration", data);
        return response;
    },
    async Update(id, data) {
        const response = await axios.post(`/api/website/admin/configuration/${id}`, data);
        return response;
    },
    async Delete(id) {
        const response = await axios.delete(`/api/website/admin/configuration/${id}`);
        return response;
    },
    async BulkUpdate(data) {
        const response = await axios.post("/api/website/admin/configuration/bulk", data);
        return response;
    },
};

// Gallery API
const Gallery = {
    async GetFolders(data = {}) {
        const response = await axios.post("/api/website/public/gallery/folders", data);
        return response;
    },
    async GetFolderByKey(folderKey, languageCode = "tr") {
        const response = await axios.get(`/api/website/public/gallery/folder/${folderKey}?lang=${languageCode}`);
        return response;
    },
    async GetFiles(data = {}) {
        const response = await axios.post("/api/website/public/gallery/files", data);
        return response;
    },
    async GetFileById(fileId, languageCode = "tr") {
        const response = await axios.get(`/api/website/public/gallery/file/${fileId}?lang=${languageCode}`);
        return response;
    },
    async IncrementViewCount(fileId) {
        const response = await axios.post(`/api/website/public/gallery/file/${fileId}/view`);
        return response;
    },
    async IncrementDownloadCount(fileId) {
        const response = await axios.post(`/api/website/public/gallery/file/${fileId}/download`);
        return response;
    },
    async GetFoldersAdmin(data = {}) {
        const response = await axios.post("/api/website/admin/gallery/folders", data);
        return response;
    },
    async GetFolderByIdAdmin(id, languageCode = "tr") {
        const response = await axios.get(`/api/website/admin/gallery/folder/${id}?lang=${languageCode}`);
        return response;
    },
    async GetFolderByKeyAdmin(folderKey, languageCode = "tr") {
        const response = await axios.get(`/api/website/admin/gallery/folder/key/${folderKey}?lang=${languageCode}`);
        return response;
    },
    async CreateFolder(data) {
        const response = await axios.post("/api/website/admin/gallery/folder", data);
        return response;
    },
    async UpdateFolder(id, data) {
        const response = await axios.post(`/api/website/admin/gallery/folder/${id}`, data);
        return response;
    },
    async DeleteFolder(id) {
        const response = await axios.post(`/api/website/admin/gallery/folder/${id}/delete`);
        return response;
    },
    async GetFilesAdmin(data = {}) {
        const response = await axios.post("/api/website/admin/gallery/files", data);
        return response;
    },
    async GetFileByIdAdmin(id, languageCode = "tr") {
        const response = await axios.get(`/api/website/admin/gallery/file/${id}?lang=${languageCode}`);
        return response;
    },
    async UploadFiles(virtualFolderId, formData) {
        const response = await axios.post(`/api/website/admin/gallery/folder/${virtualFolderId}/upload`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        return response;
    },
    prepareUploadFormData(files, metadata) {
        const formData = new FormData();
        if (files instanceof FileList) {
            Array.from(files).forEach((file) => {
                formData.append("files", file);
            });
        } else if (Array.isArray(files)) {
            files.forEach((file) => {
                formData.append("files", file);
            });
        } else {
            formData.append("files", files);
        }
        formData.append("requestJson", JSON.stringify(metadata));

        return formData;
    },
    async UpdateFile(id, data) {
        const response = await axios.post(`/api/website/admin/gallery/file/${id}`, data);
        return response;
    },
    async DeleteFile(id) {
        const response = await axios.post(`/api/website/admin/gallery/file/${id}/delete`);
        return response;
    },

    async GetFolderByKeyAdmin(folderKey, languageCode = "tr") {
        const response = await axios.get(`/api/website/admin/gallery/folder/key/${folderKey}?lang=${languageCode}`);
        return response;
    },
};

export default {
    Content,
    Language,
    Navigation,
    Configuration,
    Gallery,
};
